<template>

    <div>
        <div class="row align-items-center">
            <div class="col-xl-3  mb-2">
                <h1>{{ $t("attendance-report") }}</h1>
            </div>
            <div class="col-xl-9 mb-2 d">
                <div class="row align-items-center float-xl-right float-left">
                    <!-- Date filters-->
                    <div class="col-lg-6 align-items-center mb-2">
                        <div class="d-flex flex-wrap align-items-center justify-content-xl-end" style="gap: 1rem" >
                            <DateInput :label="$t('from')" v-model="datetime.from" @input="fetchReport()" />

                            <DateInput :label="$t('to')" v-model="datetime.to" @input="fetchReport()" />
                        </div>
                    </div>
                    <!--Status filters-->
                    <div class="col-lg-4 mt-3 mb-2 text-right">
                        <div class="btn-group float-lg-right float-left">
                            <button class="btn btn-on-good"
                                :class="{
                                    'btn-primary': status.attended,
                                    'btn-outline-secondary': !status.attended
                                }" @click="status.attended=!status.attended; fetchReport();"
                                >
                                {{ $t('attended') }}
                            </button>
                            <button class="btn btn-on-good"
                                :class="{
                                    'btn-primary': status.absent,
                                    'btn-outline-secondary': !status.absent
                                }" @click="status.absent=!status.absent; fetchReport();"
                                >
                                {{ $t('absent') }}
                            </button>
                            <button class="btn btn-on-good"
                                :class="{
                                    'btn-primary': status.unknown,
                                    'btn-outline-secondary': !status.unknown
                                }"  @click="status.unknown=!status.unknown; fetchReport();"
                                >
                                {{ $t('unknown') }}
                            </button>
                        </div>
                    </div>
                    <div class="col-lg-1 mt-3 mb-2">
                        <button class="btn btn-outline-primary ml-2 float-lg-right float-left" :title="$t('advanced-search')"
                        @click="showadvancedsearch = !showadvancedsearch">
                            <b-icon-search />
                        </button>
                    </div>
                    <div class="col-lg-1 mt-3 mb-2">
                        <button class="btn btn-outline-secondary ml-2 float-lg-right float-left" :title="$t('download')" @click="getCSV()">
                            <b-icon-download />
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Advanced search -->
        <div v-if="showadvancedsearch">
			<div class="row">
				<div class="col-md-2">
					<SelectInput
						v-model="search.programme"
						@input="fetchReport()"
						:label="$t('programme')"
						:options="$config.getValue('programmes')"
						optionLabel="Name"
						:nullable="true"
						:nullLabel="$t('all')"
					/>
				</div>
				<div class="col-md-2">
					<div class="form-group">
						<label>{{ $t("year") }}</label>
						<select class="form-control" v-model="search.year" @change="fetchReport()" :aria-label="$t('year')">
							<option :value="null">{{ $t("any") }}</option>
							<template v-if="search.programme">
								<option
								v-for="year in parseInt(search.programme.CourseLengthYears)"
								:key="year"
								:value="year"
								>{{ year }}
								</option>
								<option :value="parseInt(search.programme.CourseLengthYears) + 1">{{ $t('graduated') }}</option>
							</template>
						</select>
					</div>
				</div>
				<div class="col-md-2">
					<SelectInput
						v-model="search.group"
						@input="fetchReport()"
						:label="$t('group')"
						:options="$config.getValue('groups')"
						:indexIsValue="true"
						:nullable="true"
						:nullLabel="$t('all')"
					/>
				</div>
                <div class="col-md-3">
                    <SelectSearchInput
                        :label="$t('type')"
                        v-model="search.ttsessiontype_id"
                        :options="this.ttsessiontypes"
                        optionValue="id"
                        optionLabel="name"
                        @input="fetchReport()"
                        :nullable="true"
                    />

                </div>
                <div class="col-md-3">
                    <SelectSearchInput
                        :label="$t('room')"
                        v-model="search.ttroom_id"
                        :options="this.ttrooms"
                        optionValue="id"
                        optionLabel="name"
                        @input="fetchReport()"
                        :nullable="true"
                    />

                </div>
			</div>
            <div class="row">
                <div class="col-md-12">
                    <div class="form-group">
						<label>{{ $t("students") }}</label>
						<div
							class="pseudo-input mb-3"
						>
							<input
								type="search"
								class="input-bare w-full flex-1"
								v-if="!search.students.length"
								@click="selectStudent"
								@keyup.enter="selectStudent"
							/>
							<span
								class="badge badge-secondary badge-sm mr-2 mb-2"
								v-for="(student, index) in search.students"
								v-bind:key="index"
							>
								{{ student.displayName }}
								<a @click="removeStudent(student)"><b-icon icon="x"></b-icon></a>
							</span>
							<span
								class="badge badge-primary badge-sm mr-2 mb-2"
								v-if="search.students.length"
								@click="selectStudent"
								@keyup.enter="selectStudent"
								tabindex="0"
								:aria-label="$t('student') + ' ' + $t('add')"
							>
								<b-icon icon="plus"></b-icon> {{ $t("add") }}
							</span>
							<span
								class="badge badge-danger badge-sm mr-2 mb-2"
								v-if="search.students.length"
								@click="search.students=[];fetchReport()"
								@keyup.enter="search.students=[];fetchReport()"
								tabindex="0"
								:aria-label="$t('student') + ' ' + $t('clear-all')"
							>
								<b-icon icon="x"></b-icon> {{ $t('clear-all') }}
							</span>
						</div>
					</div>
                </div>
            </div>
		</div>

        <div class="row">
            <div class="col-12">
                <Table
                    :loading="loading"
                    :submitting="submitting"
                    :cols="[
                        { key: 'datetimeformatted', heading: $t('date-time'), size: 2 },
                        { cb: this.getSession, key: 'session', heading: $t('event'), size: 3 },
                        { cb: this.getStudent, key: 'student', heading: $t('student'), size: 3},
                        { key: 'attendancelabel', heading: $t('attendance'), size: 2},
                    ]"
                    :data="report"
                    breakpoint="md"
                    @selectItem="selectItem"
                >
                <template v-slot:rowActions="report" v-if="canEditAttendance">
                    <div>

                        <div class="btn-group" style="display: block">
                            <button
                                class="btn btn-on-good"
                                v-bind:class="{
                                    'btn-on': report.item.attendance == 1,
                                    'btn-off': report.item.attendance !== 1
                                }"
                                @click.stop="updateAttendance(report.item, true)"
                            >
                                {{ $t('here') }}
                            </button>
                            <button
                                class="btn btn-on-bad"
                                v-bind:class="{
                                    'btn-on': report.item.attendance == -1,
                                    'btn-off': report.item.attendance !== -1
                                }"
                                @click.stop="updateAttendance(report.item, false)"

                            >
                                {{ $t('absent') }} /
                                {{ $t('late') }}
                            </button>
                        </div>

                    </div>
                </template>
                </Table>
            </div>

        </div>
    </div>
</template>

<script>
import Table from "src/components/Table/Table.vue";
import Papa from 'papaparse';
import DateInput from "src/components/Inputs/DateInput";



export default {
    components: {
        Table,
        DateInput,

    },
    data() {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 31);

        return {
            report:[],
            total_count: 0,
            loading: true,
            submitting: false,
            status: {
                attended: false,
                absent: true,
                unknown: true
            },
            datetime:{
            	from: past.toISOString().split('T')[0] ,
            	to: null
            },
            timetable:{},
            showadvancedsearch:false,
            search:{
                students:[]
            },
            students:[],
            ready:false,
            ttsessiontypes:[],
            ttrooms:[],
            //item:{}
        }
    },
    mounted() {
        this.fetchReport()
        this.getStudents()
        this.getTTSessionTypes()
        this.getRooms()
    },
    methods: {
        fetchReport() {
            if (this.submitting) return
            this.submitting = true

            this.$cfetch(`${this.$settings.APIBaseURL}/attendance/report`, {
                method: 'post',
                data: {
                    ...this.status,
                    ...this.search,
                    datetime_from: this.datetime.from,
                    datetime_to: this.datetime.to
                },
            }).then((res) => {

                if (res.status !== 'SUCCESS') return
				res.result.students.map((x) => {
					if(x.ttevent_datetimefrom){
						x.datetimeformatted = this.$datetime.getLocaleStringFromISODate(x.ttevent_datetimefrom) + ' ' + this.$datetime.isoDateTimeToTime(x.ttevent_datetimefrom);
					} else {
						x.datetimeformatted = '';
					}
					if(x.attendance == 1){
						x.attendancelabel = this.$t('attended');
					} else if(x.attendance == -1){
						x.attendancelabel = `${this.$t('absent')} / ${this.$t('late')}`;
					} else {
						x.attendancelabel = this.$t('unknown');
					}
				});
                this.report= res.result.students
                this.handleReport()


            }).catch((err) => {
                this.$log.error.call(this, err)
                console.log(err)
            }).finally(() => {
                this.submitting = false
                this.loading = false

            })
        },
        updateAttendance(student , status) {
            this.$cfetch(`${this.$settings.APIBaseURL}/attendance/register/saveattendance`, {
                data: {
                    'ttevent_id': Number(student.ttevent_id),
                    'student_id': student.user_id,
                    'status': status,
                },
            }).then((res) => {
                if (res.status === "SUCCESS") {
                    if(status){
                        student.attendance = 1;
                        student.attendancelabel = this.$t('attended');
                    } else {
                        student.attendance = -1;
                        student.attendancelabel = `${this.$t('absent')} / ${this.$t('late')}`;
                    }
                } else {
                    this.somethingWentWrong();
                }
            }).catch(() => {
                this.somethingWentWrong();
            });
		},
        somethingWentWrong(){
			this.$store.commit("openDialog", {
				cancelBtn: true,
				title: this.$t("oops"),
				content: this.$t("something-went-wrong")
			});
		},
        refresh(filters) {
            this.filters = { ...this.filters, ...filters, page: 1 }
            this.listTopicComponents()
        },
        toPage(pageNo) {
            this.filters.page = pageNo
            this.listTopicComponents()
        },
        handleReport(){
            let report = this.report.map(user => {

                 //Format keys for CSV
                let csvCols = {
                    "ttevent_id": "Event ID",
                    "ttevent_datetimefrom" : "From",
                    "ttevent_datetimeto": "To",
                    "ttsessiontype_name": "Session Type",
                    "ttroom_name": "Room",
                    "ttevent_title": "Event Title",
                    "student_id":"Student ID",
                    "displayname" : "Student Name",
                    "attendancelabel": "Attendance",
                    "absencetype_title": "Absence Type",
                    "student_year": "Year"
                }

                if(this.$UserSession.hasPriv('REPORT_EMAILADDRESS')) csvCols = {...csvCols, "email" : "Email"}

                const formattedUser = {}

                for (const prop in csvCols) {// rename key if needed
                    formattedUser[csvCols[prop]] = user[prop];
                }

               // eslint-disable-next-line no-unused-vars
                let {  absencetype_title, ...rest } = formattedUser;

                if (this.status.absent) return formattedUser;

                return rest;
            });

            return report

        },
        getCSV() {
            let report = this.handleReport()
            // Get the CSV via a Promise
            return new Promise((resolve, reject) => {
                try {
                    const csv = Papa.unparse(report);
                    resolve(csv);
                } catch (error) {
                    console.error('Error unparsing CSV file:', error);
                    reject(error);
                }
            }).then(res => {
                // Create a Blob from the CSV string
                const blob = new Blob([res], { type: 'text/csv' });

                // Give it a download link
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `attendance_report.csv`;

                // Trigger download
                a.click();

                // Housekeeping
                URL.revokeObjectURL(url);
            });
        },
        getTitle(id,isTopic){
            if(!this.loading && id && this.topiccomponenttypes  && this.topics){

                let matchingObject ={}

                matchingObject  = this.topiccomponenttypes.find( item => item.id == id);

                if(isTopic ) matchingObject = this.topics.find( item => item.id == id);

                return matchingObject.title;
            }
            return ;
        },
        selectItem(item){
        	this.$router.push(`/attendance/register/${item.ttevent_id}`);
        },
        getStudents(){
            this.$users.getStudents.call(this).then(students =>{
                this.students = students.map(user => {
                        return {
                            ...user,
                            displayname: user.displayName
                        }
                    });
                this.ready = true
            });
        },
        getTTSessionTypes() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/sessiontypes/all`, {
                data: {
                },
            }).then((res) => {
                if (res.status === "SUCCESS") {
                    this.ttsessiontypes= res.results;
                } else {
                    this.somethingWentWrong();
                }
            }).catch(() => {
                this.somethingWentWrong();
            });
        },
        getRooms () {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/ttroom/listall`).then(
                (res) => {
                    if (res.status === "SUCCESS") {
                        this.ttrooms = res.results;
                    } else {
					this.$log.info.call(this, status)
				    }
                })
            .catch(error => {
                this.$log.error.call(this, error);
            });
		},
        removeStudent(selection) {
			const self = this;
			self.search.students.find((student, i) => {
				if (student.id === selection.id) {
					self.$delete(self.search.students, i);
					self.fetchReport();
					return true;
				}
			});
			return false;
		},
        selectStudent() {
			const self = this;
			this.$users.getStudents.call(this).then((students) => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					selectCallback: function (selection) {
						const isSelected = self.search.students.find((student, i) => {
							if (student.id === selection.id) {
								self.$delete(self.search.students, i);
								return true;
							}
						});
						if (!isSelected) {
							self.search.students.push(selection);
						}
						self.fetchReport();
					},
					selected: this.search.students,
					data: students,
					captionKey: "displayName",
                    subcaptionKey: "studentid",
					studentSearchFilters: true,
					selectAllBtn: true
				});
			});
		},

		getSession(item){
			return item.ttsessiontype_name + ' | ' + item.ttroom_name;
		},
		getStudent(item){
			return item.student_id + ' ' + item.displayname;
		}

    },
    computed: {
        canEditAttendance(){
        	return this.$UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN') || this.$UserSession.hasPriv('PLACEMENT_MY_PLACEMENTS') || (this.$UserSession.hasPriv.call(this, 'TIMETABLE_ATTENDANCE'))
        },


	}

}
</script>
